import { useEffect, useState } from 'react';

/** How long the greeting stays up before it fades out and gives its space back. */
export const GREETING_HOLD_MS = 6000;

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Keeps the greeting up for a beat, then takes it away.
 *
 * The greeting is a moment, not chrome — once it has been read it is just a line of text
 * between the reader and their programs. Hiding it reclaims the space.
 *
 * Two things stop it, both because collapsing the block moves everything below it:
 *
 * - **The reader has scrolled.** They're looking at something further down the page, and
 *   yanking 52px out from above it shifts what they're reading. If they've left the top,
 *   the greeting just stays where it is; they aren't looking at it anyway.
 * - **They asked for reduced motion.** No fade, no collapse, no shift — it simply stays.
 *
 * The clock also doesn't start until the tab is actually on screen, so opening the app in a
 * background tab and coming back to it later doesn't mean missing the greeting entirely.
 *
 * @param isReady whether there is a greeting to show yet
 * @returns whether the greeting should currently be rendered visible
 */
export default function useGreetingDismiss(isReady: boolean): boolean {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (!isReady || prefersReducedMotion()) return undefined;

    let timer: ReturnType<typeof setTimeout>;

    const hide = () => {
      // Collapsing while they're reading further down the page would pull it out from under
      // them. Once they've scrolled away, leave the greeting alone for good.
      if (window.scrollY > 40) return;
      setIsVisible(false);
    };

    const start = () => {
      clearTimeout(timer);
      timer = setTimeout(hide, GREETING_HOLD_MS);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') start();
    };

    if (document.visibilityState === 'visible') {
      start();
    } else {
      document.addEventListener('visibilitychange', onVisibilityChange);
    }

    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [isReady]);

  return isVisible;
}
